import {matchesFocusVisible} from '@github-ui/focus-visible'
import {testIdProps} from '@github-ui/test-id-props'
import {ChevronDownIcon, ChevronRightIcon, PaperclipIcon, XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useMemo, useRef, useState} from 'react'

import {HIGHLIGHT_SUBSET, useLowlight} from '../hooks/use-lowlight'
import {renderHastNode} from '../utils/hast-helpers'
import hljsStyles from './hljs-tokens.module.css'
import styles from './PrettifiedCodeBlock.module.css'

interface PrettifiedCodeBlockProps {
  code: string
  collapsed: boolean
  onRemove: () => void
  onToggleCollapse: () => void
  onCodeChange: (code: string) => void
  onConvertToFile?: () => void
}

function typingDelay(codeLength: number): number {
  if (codeLength > 8_500) return 750
  return 200
}

// small optimization; large blocks cause lag when typing
function useIsTyping(code: string): [boolean, () => void] {
  const [isTyping, setIsTyping] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onType = useCallback(() => {
    setIsTyping(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    const delay = typingDelay(code.length)
    timerRef.current = setTimeout(() => setIsTyping(false), delay)
  }, [code.length])

  return [isTyping, onType]
}

export function PrettifiedCodeBlock({
  code,
  collapsed,
  onRemove,
  onToggleCollapse,
  onCodeChange,
  onConvertToFile,
}: PrettifiedCodeBlockProps) {
  const label = 'Pasted content'
  const lowlight = useLowlight()
  const [initialCode] = useState(code)
  const [isTyping, onType] = useIsTyping(code)

  const detectedLanguage = useMemo(() => {
    if (!lowlight) return null
    try {
      return lowlight.highlightAuto(initialCode, {subset: HIGHLIGHT_SUBSET}).data?.language ?? null
    } catch {
      return null
    }
  }, [lowlight, initialCode])

  const highlightedNodes = useMemo(() => {
    if (!lowlight || collapsed || isTyping) return null
    try {
      const root = detectedLanguage
        ? lowlight.highlight(detectedLanguage, code)
        : lowlight.highlightAuto(code, {subset: HIGHLIGHT_SUBSET})
      if (!root.children.some(n => n.type === 'element')) return null
      return root.children.map((n, i) => renderHastNode(n, i))
    } catch {
      return null
    }
  }, [lowlight, code, collapsed, isTyping, detectedLanguage])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value
      if (!val) return onRemove()
      onCodeChange(val)
      onType()
    },
    [onCodeChange, onRemove, onType],
  )

  // Replaces `.container:has(.codeTextarea:focus-visible)` to avoid WebKit `:has()`
  // invalidation; keeps the ring keyboard-only (a `:focus-within` swap would show on click).
  const [isFocusVisible, setIsFocusVisible] = useState(false)
  const handleTextareaFocus = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocusVisible(matchesFocusVisible(e.target))
  }, [])

  return (
    <div
      className={clsx(styles.container, {
        [styles.collapsed]: collapsed,
        [styles.containerFocusVisible]: isFocusVisible,
      })}
      {...testIdProps('prettified-code-block')}
    >
      <div className={styles.header}>
        <IconButton
          size="small"
          icon={collapsed ? ChevronRightIcon : ChevronDownIcon}
          aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
          aria-expanded={!collapsed}
          onClick={onToggleCollapse}
          variant="invisible"
          tooltipDirection="n"
          className={styles.headerButton}
        />
        <span className={styles.languageLabel}>{label}</span>
        {onConvertToFile && (
          <IconButton
            size="small"
            icon={PaperclipIcon}
            aria-label={`Attach ${label} as file`}
            onClick={onConvertToFile}
            variant="invisible"
            tooltipDirection="n"
            className={styles.headerButton}
          />
        )}
        <IconButton
          size="small"
          icon={XIcon}
          aria-label={`Remove ${label}`}
          onClick={onRemove}
          variant="invisible"
          tooltipDirection="n"
          className={styles.headerButton}
        />
      </div>
      {!collapsed && (
        <div className={styles.codeWrapper}>
          {highlightedNodes && (
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            <pre className={clsx(styles.codeHighlight, hljsStyles.tokens, 'hljs')} aria-hidden>
              <code>{highlightedNodes}</code>
            </pre>
          )}
          <textarea
            className={clsx(styles.codeTextarea, {[styles.codeTextareaHighlighted]: !!highlightedNodes})}
            value={code}
            onChange={handleChange}
            onFocus={handleTextareaFocus}
            onBlur={() => setIsFocusVisible(false)}
            aria-label={label}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
        </div>
      )}
    </div>
  )
}
